/**
 * Damage feedback: directional arcs, the low-health vignette and the heartbeat.
 *
 * The arc bearing is computed from the incoming damage direction resolved against
 * the player's yaw, so an attacker behind-left puts the arc at roughly 7 o'clock.
 * `dir` is the direction the damage travelled, so the attacker is at `-dir`.
 *
 * Every arc is a pooled DOM node. Spawning writes `transform` once; the frame
 * loop then only touches `opacity`, and only for arcs that are actually alive.
 */

export const TUNING = {
  arcs: 6,
  arcLife: 1.2, // s
  arcHold: 0.28, // fraction of life at full opacity before fading
  arcPeak: 0.92,

  critFrac: 0.35, // below this the vignette + heartbeat kick in
  vignetteMax: 0.62,
  beatSlow: 1.05, // s between beats at the critical threshold
  beatFast: 0.5, // s between beats at 0 health
  flashLife: 0.34,
  flashPeak: 0.5,
};

const RAD2DEG = 180 / Math.PI;

export class DamageIndicator {
  constructor(ctx, root) {
    this.ctx = ctx;

    const layer = document.createElement('div');
    layer.className = 'dmg-layer hud-fade';

    this.pool = [];
    for (let i = 0; i < TUNING.arcs; i++) {
      const a = document.createElement('div');
      a.className = 'dmg-arc';
      a.appendChild(document.createElement('i'));
      layer.appendChild(a);
      this.pool.push({ el: a, age: -1, wrote: -1 });
    }
    root.appendChild(layer);

    this.vignette = document.createElement('div');
    this.vignette.className = 'hud-vignette';
    root.appendChild(this.vignette);

    this.flash = document.createElement('div');
    this.flash.className = 'hud-hitflash';
    root.appendChild(this.flash);

    this.frac = 1;
    this.beatTimer = 0;
    this.beatPulse = 0;
    this.flashAge = -1;
    this._wroteVig = -1;
    this._wroteFlash = -1;
    this._next = 0;

    /** Set by the HUD so the compass can draw matching hit ticks. */
    this.onBearing = null;

    ctx.events.on('player:health', (p) => {
      if (!p) return;
      const max = p.max || 100;
      this.frac = Math.max(0, Math.min(1, (p.health ?? max) / max));
    });
    ctx.events.on('player:damage', (p) => this.hit(p));
  }

  hit(p) {
    const amount = p?.amount ?? 10;
    this.flashAge = 0;
    this.flashPeak = Math.min(TUNING.flashPeak, 0.16 + amount / 90);

    const bearing = this._bearing(p?.dir);
    if (bearing === null) return;
    this.spawn(bearing);
    if (this.onBearing) this.onBearing(bearing);
  }

  /** Screen-space bearing in degrees: 0 = dead ahead, +90 = to the right. */
  _bearing(dir) {
    if (!dir || typeof dir.x !== 'number') return null;
    const yaw = this.ctx.player?.yaw ?? 0;
    // Player forward and right, matching Player.js's yaw basis.
    const fx = -Math.sin(yaw);
    const fz = -Math.cos(yaw);
    const rx = Math.cos(yaw);
    const rz = -Math.sin(yaw);
    // Attacker sits opposite the travel direction of the damage.
    const ax = -dir.x;
    const az = -dir.z;
    if (Math.abs(ax) < 1e-6 && Math.abs(az) < 1e-6) return null;
    return Math.atan2(ax * rx + az * rz, ax * fx + az * fz) * RAD2DEG;
  }

  spawn(bearingDeg) {
    // Reuse the oldest slot if everything is busy — round-robin is fine at 6.
    let slot = this.pool.find((s) => s.age < 0);
    if (!slot) {
      slot = this.pool[this._next % this.pool.length];
      this._next++;
    }
    slot.age = 0;
    slot.el.style.transform = `rotate(${bearingDeg.toFixed(1)}deg)`;
  }

  update(dt) {
    // --- arcs -------------------------------------------------------------
    for (const s of this.pool) {
      if (s.age < 0) continue;
      s.age += dt;
      if (s.age >= TUNING.arcLife) {
        s.age = -1;
        if (s.wrote !== 0) {
          s.el.style.opacity = '0';
          s.wrote = 0;
        }
        continue;
      }
      const t = s.age / TUNING.arcLife;
      const o =
        t < TUNING.arcHold
          ? TUNING.arcPeak
          : TUNING.arcPeak * (1 - (t - TUNING.arcHold) / (1 - TUNING.arcHold));
      const r = Math.round(o * 100) / 100;
      if (r !== s.wrote) {
        s.el.style.opacity = r;
        s.wrote = r;
      }
    }

    // --- hit flash --------------------------------------------------------
    if (this.flashAge >= 0) {
      this.flashAge += dt;
      let o = 0;
      if (this.flashAge < TUNING.flashLife) {
        o = this.flashPeak * (1 - this.flashAge / TUNING.flashLife);
      } else {
        this.flashAge = -1;
      }
      const r = Math.round(o * 100) / 100;
      if (r !== this._wroteFlash) {
        this.flash.style.opacity = r;
        this._wroteFlash = r;
      }
    }

    // --- low-health vignette + heartbeat ----------------------------------
    let vig = 0;
    if (this.frac < TUNING.critFrac) {
      const severity = 1 - this.frac / TUNING.critFrac;
      const period =
        TUNING.beatSlow + (TUNING.beatFast - TUNING.beatSlow) * severity;
      this.beatTimer -= dt;
      if (this.beatTimer <= 0) {
        this.beatTimer = period;
        this.beatPulse = 1;
        this.ctx.events.emit('audio:play', {
          id: 'heartbeat',
          volume: 0.35 + 0.45 * severity,
        });
      }
      this.beatPulse *= Math.exp(-dt * 6.5);
      vig = TUNING.vignetteMax * (0.42 + 0.58 * severity) * (0.55 + 0.45 * this.beatPulse);
    } else {
      this.beatTimer = 0;
      this.beatPulse = 0;
    }
    const rv = Math.round(vig * 100) / 100;
    if (rv !== this._wroteVig) {
      this.vignette.style.opacity = rv;
      this._wroteVig = rv;
    }
  }
}
